import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { QrCode, MapPin, Check, X, Plane, Shield, StopCircle, Plus, Trash2, Utensils, Coffee, Pencil, Search, Home, AlertTriangle } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import WorkModeBadge from "../components/WorkModeBadge";

// Fix default marker icons (webpack doesn't resolve them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

const TRACK_INTERVAL_MS = 5 * 60 * 1000; // 5 minuti come richiesto dall'utente

// Haversine distance in meters (client-side preflight)
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export default function CheckinPage() {
  const [qrs, setQrs] = useState([]);
  const [atts, setAtts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", radius_m: 300, address: "", city: "", province: "", zip_code: "", notes: "", manual_lat: "", manual_lng: "" });
  const [creatingQr, setCreatingQr] = useState(false);
  const [gpsError, setGpsError] = useState("");
  const [editingQr, setEditingQr] = useState(null); // {id, label, latitude, longitude, radius_m, address, ...}
  const [editGpsError, setEditGpsError] = useState("");
  const [qrAnomalies, setQrAnomalies] = useState({}); // {qrId: {invalid_ratio_pct, avg_distance_m, total, invalid}}
  // Trasferta state
  const [trasfertaPrompt, setTrasfertaPrompt] = useState(null); // {qr_id, kind}
  const [activeTrasferta, setActiveTrasferta] = useState(null);
  // Out-of-area dialog: chiede smartworking/trasferta + nota quando GPS è oltre il raggio
  const [outOfAreaPrompt, setOutOfAreaPrompt] = useState(null); // {qr_id, kind, qr_label, distance_m, radius_m, pos:{lat,lng}, note}
  // Post-save: pop-up "Anomalia segnalata all'amministratore"
  const [anomalyReported, setAnomalyReported] = useState(null); // {qr_label, distance_m, radius_m, kind, work_mode, note}
  const trackingIntervalRef = useRef(null);

  const load = async () => {
    try {
      const [q, a, t] = await Promise.all([
        api.get("/qr"),
        api.get("/attendances"),
        api.get("/trasferta/my-active").catch(()=>({ data: null })),
      ]);
      setQrs(q.data);
      setAtts(a.data);
      setActiveTrasferta(t.data);
      // Fetch anomaly stats per QR (in parallel) per warning UI
      const statsResults = await Promise.all(
        (q.data || []).map((qr) =>
          api.get(`/qr/${qr.id}/anomalies-stats?days=7`).then(r => [qr.id, r.data]).catch(()=>[qr.id, null])
        )
      );
      const map = {};
      statsResults.forEach(([id, data]) => { if (data) map[id] = data; });
      setQrAnomalies(map);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const locate = () => new Promise((res, rej) => {
    if (!("geolocation" in navigator)) return rej(new Error("Geolocation non supportata dal browser"));
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      return rej(new Error("La geolocalizzazione richiede HTTPS"));
    }
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }),
      (e) => {
        const msg = e.code === 1 ? "Permesso negato dall'utente. Apri le impostazioni del browser → consenti la localizzazione per questo sito"
                  : e.code === 2 ? "Posizione non disponibile (segnale GPS debole?)"
                  : e.code === 3 ? "Timeout: nessuna risposta dal GPS entro 15 secondi"
                  : "Errore geolocalizzazione";
        rej(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });

  const useCurrentGps = async () => {
    setGpsError("");
    try {
      const pos = await locate();
      setForm((f) => ({ ...f, manual_lat: pos.lat.toFixed(6), manual_lng: pos.lng.toFixed(6) }));
      toast.success(`Posizione rilevata (precisione ±${Math.round(pos.acc)}m)`);
    } catch (e) {
      setGpsError(e.message);
      toast.error(e.message);
    }
  };

  const createQr = async () => {
    if (!form.label) { toast.error("Inserisci il nome della sede"); return; }
    setCreatingQr(true);
    setGpsError("");
    try {
      let lat, lng;
      if (form.manual_lat && form.manual_lng) {
        lat = parseFloat(form.manual_lat);
        lng = parseFloat(form.manual_lng);
        if (Number.isNaN(lat) || Number.isNaN(lng)) throw new Error("Coordinate manuali non valide");
      } else if (form.address && form.city) {
        // Auto-geocoding fallback se utente ha compilato indirizzo+città ma non GPS
        try {
          const g = await api.post("/qr/geocode", {
            address: form.address,
            city: form.city,
            province: form.province,
            zip_code: form.zip_code,
          });
          lat = g.data.latitude;
          lng = g.data.longitude;
          toast.success(`Coordinate auto-rilevate da indirizzo · ${g.data.display_name.slice(0, 70)}…`);
        } catch (_) {
          // Fallback su GPS reale del browser
          const pos = await locate();
          lat = pos.lat;
          lng = pos.lng;
        }
      } else {
        const pos = await locate();
        lat = pos.lat;
        lng = pos.lng;
      }
      await api.post("/qr/generate", {
        label: form.label,
        latitude: lat,
        longitude: lng,
        radius_m: Number(form.radius_m),
        address: form.address,
        city: form.city,
        province: form.province,
        zip_code: form.zip_code,
        notes: form.notes,
      });
      toast.success("QR generato con posizione GPS");
      setOpen(false);
      setForm({ label: "", radius_m: 100, address: "", city: "", province: "", zip_code: "", notes: "", manual_lat: "", manual_lng: "" });
      load();
    } catch (e) {
      setGpsError(e?.message || "Errore");
      toast.error(`Errore: ${e?.message || e}`);
    } finally { setCreatingQr(false); }
  };

  const deleteQr = async (qrId) => {
    if (!confirm("Eliminare questo QR? Le timbrature passate restano.")) return;
    try {
      await api.delete(`/qr/${qrId}`);
      toast.success("QR eliminato");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const submitCheckin = async (qr_id, kind, trasferta = false, opts = {}) => {
    try {
      const pos = opts.pos || (await locate());
      const qr = qrs.find((q) => q.id === qr_id);
      // Pre-flight: se fuori area E non abbiamo già una scelta (work_mode), apri dialog
      if (qr && !opts.skip_out_check && !opts.work_mode) {
        const dist = haversine(qr.latitude, qr.longitude, pos.lat, pos.lng);
        if (dist > qr.radius_m) {
          setOutOfAreaPrompt({
            qr_id,
            kind,
            qr_label: qr.label,
            distance_m: Math.round(dist),
            radius_m: qr.radius_m,
            pos,
            note: "",
          });
          return null;
        }
      }
      const payload = {
        qr_id,
        latitude: pos.lat,
        longitude: pos.lng,
        kind,
        trasferta: !!trasferta,
      };
      if (opts.work_mode) payload.work_mode = opts.work_mode;
      if (opts.note != null) payload.note = String(opts.note).slice(0, 500);
      const r = await api.post("/attendance/checkin", payload);
      const data = r.data;
      const labels = { in: "entrata", out: "uscita", lunch_start: "inizio pausa pranzo", lunch_end: "fine pausa pranzo" };
      const lbl = labels[kind] || kind;
      if (data.valid) {
        toast.success(`Timbratura ${lbl} registrata · ${data.distance_m}m`);
      } else {
        // Apri pop-up "Anomalia segnalata all'amministratore"
        const qrInfo = qrs.find((q) => q.id === qr_id);
        setAnomalyReported({
          qr_label: qrInfo?.label || "—",
          radius_m: qrInfo?.radius_m || 0,
          distance_m: data.distance_m,
          kind,
          work_mode: opts.work_mode || null,
          note: opts.note || "",
          notified_admins: data.notified_admins || [],
        });
      }

      // Se chiude una sessione trasferta esistente
      if (data.closed_trasferta_session) {
        stopTracking();
        toast.info("Sessione trasferta chiusa · tracking GPS disattivato");
      }
      // Se apre nuova sessione trasferta
      if (data.trasferta_session_id) {
        toast.success("Trasferta avviata · tracking GPS attivo (ogni 5 min)");
        await load();
        startTracking();
      } else {
        load();
      }
      return data;
    } catch (e) { toast.error(`Errore: ${e.message || e?.response?.data?.detail || e}`); }
  };

  // Click "Entrata" → dopo check-in, chiede se è l'inizio di una trasferta (solo se non c'è già una attiva)
  const handleCheckin = async (qr_id) => {
    if (activeTrasferta) {
      // Il check-in chiuderà la trasferta — nessun prompt
      await submitCheckin(qr_id, "in", false);
      return;
    }
    setTrasfertaPrompt({ qr_id, kind: "in" });
  };

  const confirmTrasferta = async (isTrasferta) => {
    const { qr_id, kind } = trasfertaPrompt;
    setTrasfertaPrompt(null);
    await submitCheckin(qr_id, kind, isTrasferta);
  };

  const confirmOutOfArea = async (work_mode) => {
    if (!outOfAreaPrompt) return;
    const { qr_id, kind, pos, note } = outOfAreaPrompt;
    setOutOfAreaPrompt(null);
    await submitCheckin(qr_id, kind, work_mode === "trasferta", {
      pos,
      work_mode,
      note,
      skip_out_check: true,
    });
  };

  // ---- GPS tracking loop (ogni 5 minuti) ----
  const pushPoint = async () => {
    try {
      const pos = await locate();
      await api.post("/trasferta/track", { latitude: pos.lat, longitude: pos.lng, accuracy_m: pos.acc });
      await load();
    } catch (e) {
      // non blocca, solo log
      // eslint-disable-next-line no-console
      console.warn("GPS track fail:", e?.message || e);
    }
  };
  const startTracking = () => {
    if (trackingIntervalRef.current) return;
    trackingIntervalRef.current = setInterval(pushPoint, TRACK_INTERVAL_MS);
  };
  const stopTracking = () => {
    if (trackingIntervalRef.current) { clearInterval(trackingIntervalRef.current); trackingIntervalRef.current = null; }
  };

  // Reagisce al caricamento di una sessione attiva all'apertura della pagina
  useEffect(() => {
    if (activeTrasferta) startTracking();
    return () => stopTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTrasferta?.id]);

  const stopTrasfertaManually = async () => {
    try {
      await api.post("/trasferta/stop");
      toast.success("Tracking trasferta interrotto");
      stopTracking();
      setActiveTrasferta(null);
      load();
    } catch { toast.error("Errore stop"); }
  };

  // ----- Modifica QR esistente -----
  const openEditQr = (q) => {
    setEditingQr({
      id: q.id,
      label: q.label || "",
      latitude: q.latitude,
      longitude: q.longitude,
      radius_m: q.radius_m,
      address: q.address || "",
      city: q.city || "",
      province: q.province || "",
      zip_code: q.zip_code || "",
      notes: q.notes || "",
    });
    setEditGpsError("");
  };
  const useCurrentGpsEdit = async () => {
    setEditGpsError("");
    try {
      const pos = await locate();
      setEditingQr((cur) => cur ? { ...cur, latitude: pos.lat, longitude: pos.lng } : cur);
      toast.success(`Posizione aggiornata · ±${Math.round(pos.acc)}m`);
    } catch (e) { setEditGpsError(e.message || "Errore GPS"); }
  };
  const saveEditQr = async () => {
    if (!editingQr) return;
    try {
      const payload = {
        label: editingQr.label,
        latitude: parseFloat(editingQr.latitude),
        longitude: parseFloat(editingQr.longitude),
        radius_m: parseInt(editingQr.radius_m, 10) || 100,
        address: editingQr.address,
        city: editingQr.city,
        province: editingQr.province,
        zip_code: editingQr.zip_code,
        notes: editingQr.notes,
      };
      await api.patch(`/qr/${editingQr.id}`, payload);
      toast.success("QR aggiornato");
      setEditingQr(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    }
  };

  // 1-click fix sulla card QR (no dialog)
  const oneClickFixCoords = async (qr) => {
    if (!qr.address) {
      toast.error("Manca l'indirizzo sul QR · usa la matita per aggiungerlo");
      return;
    }
    if (!window.confirm(`Auto-correggere le coordinate del QR "${qr.label}" da indirizzo "${qr.address}, ${qr.city || ""}"?\n\nLe nuove coordinate verranno cercate via OpenStreetMap.`)) return;
    try {
      const r = await api.post(`/qr/${qr.id}/fix-coords`);
      const shifted = r.data.shifted_by_m;
      toast.success(`Coordinate aggiornate · spostate di ${shifted >= 1000 ? (shifted/1000).toFixed(1) + " km" : Math.round(shifted) + " m"}`, { duration: 6000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore correzione");
    }
  };
  const geocodeEditAddress = async () => {
    if (!editingQr) return;
    if (!editingQr.address && !editingQr.city) {
      setEditGpsError("Compila almeno indirizzo o città prima di geocodificare");
      return;
    }
    setEditGpsError("");
    try {
      const r = await api.post("/qr/geocode", {
        address: editingQr.address || "",
        city: editingQr.city || "",
        province: editingQr.province || "",
        zip_code: editingQr.zip_code || "",
      });
      setEditingQr({ ...editingQr, latitude: r.data.latitude, longitude: r.data.longitude });
      toast.success(`Trovato: ${r.data.display_name.slice(0, 80)}…`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore geocodifica";
      setEditGpsError(detail);
      toast.error(detail);
    }
  };

  // Default map center: prima QR, altrimenti Roma
  const mapCenter = qrs.length > 0
    ? [qrs[0].latitude, qrs[0].longitude]
    : [41.9028, 12.4964];
  const mapPoints = qrs.map(q => ({ lat: q.latitude, lng: q.longitude }));
  const trailPoints = activeTrasferta?.points?.map(p => [p.lat, p.lng]) || [];

  return (
    <div className="p-4 md:p-12" data-testid="checkin-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Presenze QR + Geolocalizzazione + Trasferte</div>
          <h1 className="font-display text-4xl">Check-in & Check-out</h1>
          <p className="text-[#555] mt-1 text-sm">Mappa sedi · timbratura GPS · tracking trasferte GDPR-compliant.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-qr-btn" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] btn-glow"><Plus className="w-4 h-4 mr-1"/> Nuovo indirizzo</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo indirizzo · Genera QR</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome sede *</Label>
                <Input data-testid="qr-label" className="rounded-xl mt-1" value={form.label} onChange={(e)=>setForm({...form, label: e.target.value})} placeholder="Es. Ufficio Milano"/>
              </div>
              <div>
                <Label>Indirizzo (via, civico)</Label>
                <Input data-testid="qr-address" className="rounded-xl mt-1" value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} placeholder="Via Roma, 12"/>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label>Città</Label>
                  <Input data-testid="qr-city" className="rounded-xl mt-1" value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} placeholder="Milano"/>
                </div>
                <div>
                  <Label>Provincia</Label>
                  <Input data-testid="qr-province" className="rounded-xl mt-1 uppercase font-mono" value={form.province} onChange={(e)=>setForm({...form, province: e.target.value.toUpperCase()})} maxLength={2} placeholder="MI"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>CAP</Label>
                  <Input data-testid="qr-zip" className="rounded-xl mt-1 font-mono" value={form.zip_code} onChange={(e)=>setForm({...form, zip_code: e.target.value})} maxLength={5} placeholder="20100"/>
                </div>
                <div>
                  <Label>Raggio (metri)</Label>
                  <Input data-testid="qr-radius" type="number" className="rounded-xl mt-1" value={form.radius_m} onChange={(e)=>setForm({...form, radius_m: e.target.value})}/>
                </div>
              </div>
              <div>
                <Label>Note (opzionale)</Label>
                <Input data-testid="qr-notes" className="rounded-xl mt-1" value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} placeholder="Es. Ingresso principale"/>
              </div>
              <p className="text-xs text-[#757575]">La posizione GPS verrà usata come centro della sede. Concedi l'accesso alla geolocalizzazione oppure inserisci le coordinate manualmente.</p>

              <div className="bg-[#0A0E27] border border-[#1F2540] rounded-xl p-3 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-bold">Coordinate sede</div>
                  <Button type="button" onClick={useCurrentGps} variant="outline" data-testid="qr-use-gps" className="rounded-full border-[#3B82F6] text-[#7C3AED] hover:bg-[#1E2347] h-9 text-xs">
                    <MapPin className="w-3.5 h-3.5 mr-1.5"/> Usa posizione attuale
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Latitudine</Label>
                    <Input data-testid="qr-lat" className="rounded-xl mt-1" value={form.manual_lat} onChange={(e)=>setForm({...form, manual_lat: e.target.value})} placeholder="45.464203" inputMode="decimal"/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Longitudine</Label>
                    <Input data-testid="qr-lng" className="rounded-xl mt-1" value={form.manual_lng} onChange={(e)=>setForm({...form, manual_lng: e.target.value})} placeholder="9.189982" inputMode="decimal"/>
                  </div>
                </div>
                {gpsError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-2 flex items-start gap-2" data-testid="qr-gps-error">
                    <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"/>
                    <span>{gpsError}</span>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={createQr} disabled={creatingQr} data-testid="qr-create" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">
                {creatingQr ? "Generazione…" : "Genera QR"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {activeTrasferta && (
        <div className="card-smart p-4 mb-6 bg-gradient-to-r from-[#1E2347] to-[#0A0E27] border-l-4 border-[#3B82F6] flex items-center gap-4 flex-wrap" data-testid="active-trasferta-banner">
          <Plane className="w-6 h-6 text-[#3B82F6]"/>
          <div className="flex-1 min-w-40">
            <div className="font-semibold">Trasferta attiva · {activeTrasferta.start_qr_label}</div>
            <div className="text-xs text-[#757575]">Dal {new Date(activeTrasferta.started_at).toLocaleString("it-IT")} · {activeTrasferta.points?.length || 0} punti GPS · tracking ogni 5 min</div>
          </div>
          <div className="text-xs text-[#757575] flex items-center gap-1"><Shield className="w-3 h-3"/> GDPR: dati conservati fino a fine mese</div>
          <Button onClick={stopTrasfertaManually} variant="outline" data-testid="stop-trasferta" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <StopCircle className="w-4 h-4 mr-1"/> Interrompi tracking
          </Button>
        </div>
      )}

      {/* MAPPA */}
      <div className="card-smart overflow-hidden mb-8" data-testid="qr-map">
        <div className="p-4 border-b border-[#1F2540] flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#3B82F6]"/>
          <h3 className="font-display text-xl">Mappa sedi & timbrature</h3>
          <span className="text-xs text-[#757575] ml-auto">{qrs.length} punti · OpenStreetMap</span>
        </div>
        <div style={{ height: 380 }}>
          <MapContainer center={mapCenter} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {qrs.map((q) => (
              <React.Fragment key={q.id}>
                <Marker position={[q.latitude, q.longitude]}>
                  <Popup>
                    <div className="space-y-1">
                      <div className="font-semibold">{q.label}</div>
                      <div className="text-xs text-gray-500">Raggio: {q.radius_m}m</div>
                      <div className="text-xs text-gray-500">{q.latitude.toFixed(5)}, {q.longitude.toFixed(5)}</div>
                    </div>
                  </Popup>
                </Marker>
                <Circle center={[q.latitude, q.longitude]} radius={q.radius_m} pathOptions={{ color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.1, weight: 2 }}/>
              </React.Fragment>
            ))}
            {trailPoints.length > 1 && (
              <Polyline positions={trailPoints} pathOptions={{ color: "#DC2626", weight: 3, dashArray: "6 6" }}/>
            )}
            <FitBounds points={[...mapPoints, ...trailPoints.map(([lat,lng])=>({lat, lng}))]}/>
          </MapContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-7 card-smart p-6">
          <h3 className="font-display text-xl mb-4">QR delle sedi</h3>
          {qrs.length === 0 && <div className="text-sm text-[#757575]">Nessun QR creato. Genera il primo per vederlo sulla mappa.</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {qrs.map((q) => (
              <div key={q.id} className="border border-[#1F2540] rounded-xl p-4 bg-white" data-testid={`qr-${q.id}`}>
                <img src={`data:image/png;base64,${q.png_base64 || ""}`} alt={q.label} className="w-full aspect-square bg-white rounded-lg" style={{ imageRendering: "pixelated" }}/>
                <div className="mt-3">
                  <div className="font-semibold">{q.label}</div>
                  {(q.address || q.city) && <div className="text-xs text-[#444] mt-0.5">{q.address}{q.address && q.city ? ", " : ""}{q.city}{q.province ? ` (${q.province})` : ""}</div>}
                  <div className="text-xs text-[#757575] flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3"/> raggio {q.radius_m}m</div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button onClick={()=>handleCheckin(q.id)} data-testid={`checkin-${q.id}`} className="rounded-full flex-1 bg-[#3B82F6] hover:bg-[#7C3AED] h-9 text-xs">Entrata</Button>
                  <Button onClick={()=>submitCheckin(q.id, "out", false)} data-testid={`checkout-${q.id}`} variant="outline" className="rounded-full flex-1 h-9 text-xs">Uscita</Button>
                  <Button onClick={()=>openEditQr(q)} data-testid={`edit-qr-${q.id}`} variant="ghost" className="rounded-full h-9 px-2 text-[#0A0A0A] hover:bg-[#1E2347]" title="Modifica coordinate / raggio">
                    <Pencil className="w-3.5 h-3.5"/>
                  </Button>
                  <Button onClick={()=>deleteQr(q.id)} data-testid={`delete-qr-${q.id}`} variant="ghost" className="rounded-full h-9 px-2 text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </Button>
                </div>
                {qrAnomalies[q.id] && qrAnomalies[q.id].total > 0 && qrAnomalies[q.id].invalid_ratio_pct >= 50 && (
                  <div className="mt-3 rounded-xl border-2 border-red-300 bg-red-50 p-3 text-xs" data-testid={`qr-warning-${q.id}`}>
                    <div className="font-bold text-red-800 flex items-center gap-1.5 mb-1">
                      <Shield className="w-3.5 h-3.5"/> Allerta GPS · {qrAnomalies[q.id].invalid_ratio_pct}% timbrature fuori area
                    </div>
                    <div className="text-red-700 mb-2">
                      {qrAnomalies[q.id].invalid} su {qrAnomalies[q.id].total} timbrature degli ultimi 7 giorni risultano fuori area (distanza media {qrAnomalies[q.id].avg_distance_m}m). Le coordinate del QR potrebbero essere sbagliate.
                    </div>
                    {q.address ? (
                      <Button
                        onClick={() => oneClickFixCoords(q)}
                        data-testid={`fix-coords-${q.id}`}
                        size="sm"
                        className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
                      >
                        <Search className="w-3 h-3 mr-1.5"/> Auto-correggi da indirizzo "{q.address}"
                      </Button>
                    ) : (
                      <Button
                        onClick={() => openEditQr(q)}
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full border-red-600 text-red-700 h-8 text-xs"
                      >
                        <Pencil className="w-3 h-3 mr-1.5"/> Aggiungi indirizzo per auto-correggere
                      </Button>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  <Button
                    onClick={()=>submitCheckin(q.id, "lunch_start", false)}
                    data-testid={`lunch-start-${q.id}`}
                    variant="outline"
                    className="rounded-full flex-1 h-9 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                  >
                    <Utensils className="w-3.5 h-3.5 mr-1"/> Pausa pranzo
                  </Button>
                  <Button
                    onClick={()=>submitCheckin(q.id, "lunch_end", false)}
                    data-testid={`lunch-end-${q.id}`}
                    variant="outline"
                    className="rounded-full flex-1 h-9 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                  >
                    <Coffee className="w-3.5 h-3.5 mr-1"/> Fine pausa
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:col-span-5 card-smart p-6">
          <h3 className="font-display text-xl mb-4">Timbrature recenti</h3>
          {atts.length === 0 ? (
            <div className="text-sm text-[#757575]">Nessuna timbratura registrata.</div>
          ) : (
            <ul className="divide-y divide-[#1F2540] max-h-[520px] overflow-auto">
              {atts.slice(0, 30).map((a) => (
                <li key={a.id} className="py-3 flex items-center justify-between gap-2" data-testid={`attendance-${a.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate flex items-center gap-2 flex-wrap">
                      <span>{a.user_name} · <span className="text-[#3B82F6]">{({in:"Entrata", out:"Uscita", lunch_start:"Inizio pausa pranzo", lunch_end:"Fine pausa pranzo"})[a.kind] || a.kind}</span></span>
                      <WorkModeBadge mode={a.work_mode}/>
                    </div>
                    <div className="text-xs text-[#757575] truncate">{new Date(a.timestamp).toLocaleString("it-IT")} · {a.qr_label} · {a.distance_m}m</div>
                    {a.note && <div className="text-xs text-[#444] italic mt-0.5 line-clamp-2" title={a.note}>“{a.note}”</div>}
                  </div>
                  {a.valid
                    ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0"><Check className="w-3 h-3"/>OK</span>
                    : <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0"><X className="w-3 h-3"/>Fuori</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Dialog trasferta con consenso GDPR */}
      <Dialog open={!!trasfertaPrompt} onOpenChange={(o)=>{ if (!o) setTrasfertaPrompt(null); }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="trasferta-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Plane className="w-6 h-6 text-[#3B82F6]"/> Stai partendo per una trasferta?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p>Se confermi, attiveremo il <strong>tracking GPS della trasferta</strong> fino alla tua prossima timbratura di lavoro.</p>
            <div className="bg-[#0A0E27] border border-[#1F2540] rounded-xl p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-[#3B82F6] mt-0.5 flex-shrink-0"/>
                <div className="text-xs leading-relaxed text-[#555]">
                  <strong>Informativa GDPR (Reg. UE 2016/679):</strong><br/>
                  · Finalità: verifica della trasferta lavorativa e rimborso spese<br/>
                  · Base giuridica: esecuzione del contratto di lavoro (art. 6.1.b)<br/>
                  · Frequenza rilevamento: 1 punto ogni 5 minuti<br/>
                  · Conservazione: <strong>fino a fine del mese corrente</strong>, poi cancellazione automatica<br/>
                  · Titolare: la tua azienda · Responsabile: Verixa<br/>
                  · Diritti: puoi interrompere il tracking in qualsiasi momento e richiedere la cancellazione dei dati
                </div>
              </div>
            </div>
            <p className="text-[#757575] text-xs">Se non sei in trasferta clicca <em>No</em>: verrà registrata solo la normale timbratura d'ingresso, senza tracking.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={()=>confirmTrasferta(false)} variant="outline" data-testid="trasferta-no" className="rounded-full">No, timbratura normale</Button>
            <Button onClick={()=>confirmTrasferta(true)} data-testid="trasferta-yes" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]"><Plane className="w-4 h-4 mr-1"/> Sì, avvia trasferta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog FUORI AREA: chiede smartworking/trasferta + nota */}
      <Dialog open={!!outOfAreaPrompt} onOpenChange={(o)=>{ if (!o) setOutOfAreaPrompt(null); }}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[92vh] overflow-y-auto" data-testid="out-of-area-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl md:text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6 text-amber-600"/> Sei fuori dalla sede
            </DialogTitle>
          </DialogHeader>
          {outOfAreaPrompt && (
            <div className="space-y-4 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs leading-relaxed text-amber-900">
                <div className="font-bold mb-1">📍 Distanza: {outOfAreaPrompt.distance_m}m dal QR "{outOfAreaPrompt.qr_label}"</div>
                <div>Il raggio consentito è di {outOfAreaPrompt.radius_m}m. Per registrare comunque la timbratura, indica la tua modalità di lavoro:</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#999] font-bold">Modalità di lavoro</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => confirmOutOfArea("smartworking")}
                    data-testid="ooa-smartworking"
                    className="rounded-2xl border-2 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 transition p-4 flex flex-col items-center gap-2 text-center"
                  >
                    <Home className="w-7 h-7 text-emerald-600"/>
                    <div className="font-semibold text-emerald-900">Smart working</div>
                    <div className="text-[11px] text-[#757575]">Lavoro da remoto/casa</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => confirmOutOfArea("trasferta")}
                    data-testid="ooa-trasferta"
                    className="rounded-2xl border-2 border-[#3B82F6]/40 hover:border-[#3B82F6] hover:bg-[#1E2347] transition p-4 flex flex-col items-center gap-2 text-center"
                  >
                    <Plane className="w-7 h-7 text-[#3B82F6]"/>
                    <div className="font-semibold text-[#0A0A0A]">Trasferta</div>
                    <div className="text-[11px] text-[#757575]">Cliente/cantiere {outOfAreaPrompt.kind === "in" ? "· tracking GPS" : ""}</div>
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="ooa-note" className="text-xs uppercase tracking-widest text-[#999] font-bold">Nota (facoltativa)</Label>
                <textarea
                  id="ooa-note"
                  data-testid="ooa-note"
                  rows={3}
                  maxLength={500}
                  value={outOfAreaPrompt.note}
                  onChange={(e)=> setOutOfAreaPrompt({ ...outOfAreaPrompt, note: e.target.value })}
                  placeholder="Es. visita cliente Acme · cantiere Roma · lavoro da casa giovedì…"
                  className="w-full mt-1 rounded-xl border border-[#1F2540] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6] resize-none"
                />
                <div className="text-[10px] text-[#999] mt-1 text-right">{outOfAreaPrompt.note.length}/500</div>
              </div>
              <div className="text-[11px] text-[#757575] leading-snug">
                <Shield className="w-3 h-3 inline mr-1"/> La timbratura verrà registrata come "fuori area" con la modalità da te dichiarata e la nota. L'admin riceverà una notifica.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
            <Button onClick={()=>setOutOfAreaPrompt(null)} variant="ghost" data-testid="ooa-cancel" className="rounded-full">Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pop-up "Anomalia segnalata all'amministratore" — appare DOPO la scelta smart/trasferta */}
      <Dialog open={!!anomalyReported} onOpenChange={(o)=>{ if (!o) setAnomalyReported(null); }}>
        <DialogContent className="rounded-2xl max-w-md border-2 border-red-500" data-testid="anomaly-reported-dialog">
          <div className="text-center py-4">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <AlertTriangle className="w-10 h-10 text-red-600"/>
            </div>
            <h2 className="font-display text-2xl text-red-700 mb-2">Anomalia segnalata</h2>
            <p className="text-sm text-[#444] mb-3">
              La timbratura è stata registrata <strong>fuori dall'area consentita</strong> e segnalata automaticamente all'amministratore.
            </p>
            {anomalyReported && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-left text-sm space-y-1 mb-3">
                <div><span className="text-[#757575]">Sede QR:</span> <strong>{anomalyReported.qr_label}</strong></div>
                <div><span className="text-[#757575]">Distanza:</span> <strong className="text-red-700">{anomalyReported.distance_m}m</strong> (max {anomalyReported.radius_m}m)</div>
                <div><span className="text-[#757575]">Tipo:</span> {({in:"Entrata", out:"Uscita", lunch_start:"Pausa pranzo", lunch_end:"Fine pausa"})[anomalyReported.kind] || anomalyReported.kind}</div>
                {anomalyReported.work_mode && (
                  <div className="flex items-center gap-2"><span className="text-[#757575]">Modalità dichiarata:</span> <WorkModeBadge mode={anomalyReported.work_mode}/></div>
                )}
                {anomalyReported.note && (
                  <div className="pt-2 border-t border-red-200 mt-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#757575] font-bold mb-0.5">Nota</div>
                    <div className="italic text-[#0A0A0A]">“{anomalyReported.note}”</div>
                  </div>
                )}
              </div>
            )}
            <div className="bg-[#1E2347] border border-[#3B82F6]/30 rounded-xl p-3 text-xs text-[#444] flex items-start gap-2 text-left">
              <Shield className="w-4 h-4 text-[#3B82F6] mt-0.5 flex-shrink-0"/>
              <div>
                {anomalyReported?.notified_admins?.length > 0 ? (
                  <>
                    Notifica inviata a: {anomalyReported.notified_admins.slice(0, 3).map((a, i) => (
                      <span key={a.email || i}>
                        <strong className="text-[#0A0A0A]">{a.full_name}</strong>{i < Math.min(anomalyReported.notified_admins.length, 3) - 1 ? ", " : ""}
                      </span>
                    ))}
                    {anomalyReported.notified_admins.length > 3 && <span> e altri {anomalyReported.notified_admins.length - 3}</span>}.
                    <div className="mt-1">L'email contiene tutti i dettagli della timbratura, la modalità dichiarata e la nota.</div>
                  </>
                ) : (
                  <>L'amministratore della tua azienda ha ricevuto un'email di notifica con i dettagli della timbratura, la modalità dichiarata e la nota.</>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={()=>setAnomalyReported(null)} data-testid="close-anomaly-reported" className="w-full rounded-full bg-red-600 hover:bg-red-700">Ho capito</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog modifica QR (coords, raggio, etichetta) */}
      <Dialog open={!!editingQr} onOpenChange={(o)=>{ if (!o) setEditingQr(null); }}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><Pencil className="w-6 h-6 text-[#3B82F6]"/> Modifica sede QR</DialogTitle>
          </DialogHeader>
          {editingQr && (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900" data-testid="edit-qr-help">
                <strong>💡 GPS fuori area?</strong> Compila <strong>Indirizzo</strong> e <strong>Città</strong>, poi clicca <strong>"Trova da indirizzo"</strong>: il sistema cercherà automaticamente le coordinate corrette via OpenStreetMap. Per un margine di sicurezza imposta il raggio a <strong>300–500m</strong>.
              </div>
              <div>
                <Label>Nome sede</Label>
                <Input data-testid="edit-qr-label" className="rounded-xl mt-1" value={editingQr.label} onChange={(e)=>setEditingQr({...editingQr, label: e.target.value})}/>
              </div>
              <div>
                <Label>Indirizzo</Label>
                <Input data-testid="edit-qr-address" className="rounded-xl mt-1" value={editingQr.address} onChange={(e)=>setEditingQr({...editingQr, address: e.target.value})}/>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label>Città</Label>
                  <Input className="rounded-xl mt-1" value={editingQr.city} onChange={(e)=>setEditingQr({...editingQr, city: e.target.value})}/>
                </div>
                <div>
                  <Label>Prov.</Label>
                  <Input className="rounded-xl mt-1 uppercase font-mono" value={editingQr.province} onChange={(e)=>setEditingQr({...editingQr, province: e.target.value.toUpperCase()})} maxLength={2}/>
                </div>
              </div>
              <div className="bg-[#0A0E27] border-2 border-[#3B82F6]/30 rounded-xl p-3 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs uppercase tracking-widest text-[#3B82F6] font-bold">Coordinate & Raggio GPS</div>
                  <div className="flex gap-2 flex-wrap">
                    <Button type="button" onClick={geocodeEditAddress} variant="outline" data-testid="edit-qr-geocode" className="rounded-full border-emerald-500 text-emerald-700 hover:bg-emerald-50 h-9 text-xs">
                      <Search className="w-3.5 h-3.5 mr-1.5"/> Trova da indirizzo
                    </Button>
                    <Button type="button" onClick={useCurrentGpsEdit} variant="outline" data-testid="edit-qr-use-gps" className="rounded-full border-[#3B82F6] text-[#7C3AED] hover:bg-[#1E2347] h-9 text-xs">
                      <MapPin className="w-3.5 h-3.5 mr-1.5"/> Usa la mia posizione
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Latitudine</Label>
                    <Input data-testid="edit-qr-lat" className="rounded-xl mt-1" value={editingQr.latitude} onChange={(e)=>setEditingQr({...editingQr, latitude: e.target.value})} inputMode="decimal"/>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-[#999]">Longitudine</Label>
                    <Input data-testid="edit-qr-lng" className="rounded-xl mt-1" value={editingQr.longitude} onChange={(e)=>setEditingQr({...editingQr, longitude: e.target.value})} inputMode="decimal"/>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#999]">Raggio consentito (metri)</Label>
                  <Input data-testid="edit-qr-radius" type="number" className="rounded-xl mt-1" value={editingQr.radius_m} onChange={(e)=>setEditingQr({...editingQr, radius_m: e.target.value})}/>
                  <p className="text-[11px] text-[#757575] mt-1">Consigliato: 100–200m per uffici · 200–500m per cantieri · 50m per check-in molto stretti</p>
                </div>
                {editGpsError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-2 flex items-start gap-2">
                    <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"/><span>{editGpsError}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button onClick={()=>setEditingQr(null)} variant="ghost" className="rounded-full">Annulla</Button>
            <Button onClick={saveEditQr} data-testid="edit-qr-save" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">Salva modifiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
